import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { MessageCircle } from 'lucide-react';

/**
 * Composant global monte une fois dans App.js.
 * Poll toutes les 45s pour recuperer les nouveaux messages WhatsApp recus
 * et affiche un toast clickable redirigeant vers /influence-marketing.
 * Joue un chime discret si activeProperty user_id_chime_disabled !== 'true'.
 *
 * Stockage local de la derniere fois qu'on a vu un msg pour eviter doublons.
 */
const STORAGE_KEY = 'boufi_whatsapp_last_seen_ts';
const SOUND_KEY = 'boufi_whatsapp_chime_disabled';
const POLL_INTERVAL_MS = 45_000;

function playChime() {
  if (typeof window === 'undefined') return;
  if (localStorage.getItem(SOUND_KEY) === 'true') return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.3);
  } catch {
    // ignore audio errors
  }
}

export default function WhatsAppNotifications() {
  const navigate = useNavigate();
  const lastSeenRef = useRef(localStorage.getItem(STORAGE_KEY) || new Date().toISOString());
  const intervalRef = useRef(null);
  const initialLoadDoneRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const r = await api.get('/influencers/notifications/new-messages', {
          params: { since: lastSeenRef.current },
        });
        if (cancelled) return;
        const msgs = r.data?.messages || [];

        // Premier load : on ne montre PAS les notifs historiques mais on enregistre le timestamp
        if (!initialLoadDoneRef.current) {
          initialLoadDoneRef.current = true;
          if (r.data?.server_time) {
            lastSeenRef.current = r.data.server_time;
            localStorage.setItem(STORAGE_KEY, lastSeenRef.current);
          }
          return;
        }

        if (msgs.length > 0) {
          playChime();
          // Affiche un toast par msg (max 3 d'affilee pour ne pas spammer)
          msgs.slice(0, 3).forEach((m) => {
            const who = m.influencer_pseudo || m.influencer_nom || 'Influenceuse';
            const preview = (m.content || '').slice(0, 90) + ((m.content || '').length > 90 ? '...' : '');
            toast.success(
              `${who} vient de te repondre`,
              {
                description: preview,
                duration: 8000,
                action: {
                  label: 'Voir',
                  onClick: () => navigate('/influence-marketing'),
                },
                icon: <MessageCircle className="w-4 h-4 text-[#25D366]" />,
              }
            );
          });
          if (msgs.length > 3) {
            toast(`+${msgs.length - 3} autres messages WhatsApp`, { duration: 5000 });
          }
          // Met a jour le ts
          const newest = msgs[0].ts;
          if (newest && newest > lastSeenRef.current) {
            lastSeenRef.current = newest;
            localStorage.setItem(STORAGE_KEY, lastSeenRef.current);
          }
        } else if (r.data?.server_time && !lastSeenRef.current) {
          lastSeenRef.current = r.data.server_time;
          localStorage.setItem(STORAGE_KEY, lastSeenRef.current);
        }
      } catch {
        // silent fail (user might be logged out / network blip)
      }
    };

    // Premier poll au montage
    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [navigate]);

  return null;
}
